import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DashboardOutlined, TeamOutlined, UserOutlined, SettingOutlined, CalendarOutlined, BookOutlined, FileTextOutlined, CheckCircleOutlined, DollarOutlined, SolutionOutlined, BarChartOutlined, LockOutlined, DashOutlined, InboxOutlined, HistoryOutlined, CarOutlined, MessageOutlined, MailOutlined, SkinOutlined, ReadOutlined, HomeOutlined, SwapOutlined, KeyOutlined, WhatsAppOutlined, SmileOutlined, HeartOutlined, SafetyOutlined, EnvironmentOutlined } from '@ant-design/icons';
import MainLayout from '../components/MainLayout';
import { useSettings } from '../context/SettingsContext';

const AdminLayout = ({ children, user }) => {
    const { t } = useTranslation();
    const { settings } = useSettings();

    const generateAdminMenu = useCallback((closeMobileDrawer) => {
        const isAdmin = user?.role === 'super_admin' || user?.role === 'principal' || user?.role === 'developer';
        const modules = settings?.modules_enabled || {};
        const isKg = settings?.system_template === 'kg';

        const isEnabled = (moduleKey) => {
            if (!modules) return true;
            return modules[moduleKey] !== false;
        };

        const createMenuItem = (key, icon, label, moduleKey) => {
            return {
                key, icon, label: <Link to={key} onClick={closeMobileDrawer}>{label}</Link>
            };
        };

        const createSubMenu = (key, icon, label, children) => {
            if (!children || children.length === 0) return null;
            return { key, icon, label, children };
        };

        const items = [];

        // Group 1: Core Dashboards
        if (isEnabled('dashboard')) {
            items.push(createMenuItem('/', <DashboardOutlined />, t('common.dashboard'), 'dashboard'));
        }
        if (isEnabled('calendar')) {
            items.push(createMenuItem('/calendar', <CalendarOutlined />, t('menu.schoolCalendar'), 'calendar'));
        }

        items.push({ type: 'divider' });

        // Group 2: Student Sphere
        const studentItems = [];
        if (isEnabled('students')) studentItems.push(createMenuItem('/students', <UserOutlined />, t('common.students'), 'students'));
        if (isEnabled('attendance')) studentItems.push(createMenuItem('/attendance', <CheckCircleOutlined />, t('common.attendance'), 'attendance'));
        if (isEnabled('inquiries')) studentItems.push(createMenuItem('/inquiries', <InboxOutlined />, t('menu.admissionInquiries'), 'inquiries'));
        studentItems.push(createMenuItem('/alumni', <HistoryOutlined />, 'Alumni Directory', 'students'));

        if (studentItems.length > 0) {
            items.push(createSubMenu('student-hub', <TeamOutlined />, 'Student Hub', studentItems));
        }

        if (isKg) {
            const kgItems = [
                createMenuItem('/kg/daily-logs', <SmileOutlined />, 'Daily Logs', 'students'),
                createMenuItem('/kg/milestones', <HeartOutlined />, 'Milestones', 'students'),
                createMenuItem('/kg/pickups', <SafetyOutlined />, 'Safety & Pickups', 'students'),
                createMenuItem('/kg/reports', <BarChartOutlined />, 'Developmental Reports', 'reports')
            ];
            items.push(createSubMenu('kg-mgmt', <SmileOutlined />, 'KG / Early Years', kgItems));
        }

        // Group 3: Academics & Assessment
        const academicSubItems = [];
        if (isEnabled('classes')) academicSubItems.push(createMenuItem('/classes', <TeamOutlined />, t('menu.classes'), 'classes'));
        if (isEnabled('subjects')) academicSubItems.push(createMenuItem('/subjects', <ReadOutlined />, t('menu.subjects'), 'subjects'));
        if (isEnabled('timetable')) academicSubItems.push(createMenuItem('/timetable', <CalendarOutlined />, t('menu.timetable'), 'timetable'));

        if (isEnabled('assignments')) {
            academicSubItems.push(createSubMenu('assignment-sub', <SolutionOutlined />, t('menu.assignments'), [
                createMenuItem('/class-subjects', <BookOutlined />, 'Class Subjects', 'classes'),
                createMenuItem('/assignments/teacher-subjects', <UserOutlined />, 'Teacher Subjects', 'assignments'),
                createMenuItem('/assignments/class-assignments', <TeamOutlined />, 'Class Assignments', 'assignments'),
                createMenuItem('/assignments/tutor-groups', <SolutionOutlined />, 'Tutor Groups', 'assignments')
            ]));
        }
        if (isEnabled('academic-year')) {
            academicSubItems.push(createSubMenu('academic-year-mgmt', <HistoryOutlined />, t('menu.academicYears'), [
                createMenuItem('/academic-years/history', <HistoryOutlined />, 'Year History', 'academic-year'),
                createMenuItem('/academic-years/promotion-paths', <TeamOutlined />, 'Promotion Paths', 'academic-year')
            ]));
        }

        if (academicSubItems.length > 0) {
            items.push(createSubMenu('academic-mgmt', <BookOutlined />, 'Academics & Curriculum', academicSubItems));
        }

        const assessmentSubItems = [];
        if (isEnabled('exams')) {
            assessmentSubItems.push(createMenuItem('/exams', <FileTextOutlined />, t('menu.exams'), 'exams'));
        }
        if (isAdmin) {
            assessmentSubItems.push(createMenuItem('/exam-types', <FileTextOutlined />, 'Exam Types', 'exams'));
            assessmentSubItems.push(createMenuItem('/grades', <SolutionOutlined />, 'Grade Definitions', 'exams'));
        }
        if (isEnabled('marks')) {
            assessmentSubItems.push(createMenuItem('/marks', <BookOutlined />, t('common.marks'), 'marks'));
        }

        if (assessmentSubItems.length > 0) {
            items.push(createSubMenu('assessment-mgmt', <FileTextOutlined />, 'Assessment & Marks', assessmentSubItems));
        }

        items.push({ type: 'divider' });

        // Group 4: Administrative Operations
        const hrSubItems = [];
        if (isEnabled('users')) hrSubItems.push(createMenuItem('/users', <UserOutlined />, 'User Accounts', 'users'));
        if (isEnabled('hr')) {
            hrSubItems.push(createMenuItem('/hr/staff', <TeamOutlined />, 'Staff Directory', 'hr'));
            hrSubItems.push(createMenuItem('/hr/leaves', <CalendarOutlined />, 'Leave Management', 'hr'));
        }
        if (hrSubItems.length > 0) {
            items.push(createSubMenu('hr-mgmt', <SolutionOutlined />, 'Human Resources', hrSubItems));
        }

        const financeSubItems = [];
        if (isEnabled('fees')) {
            financeSubItems.push(createMenuItem('/fees/collection', <DollarOutlined />, 'Fee Collection', 'fees'));
            financeSubItems.push(createMenuItem('/fees/payments', <DollarOutlined />, 'Collect Payments', 'fees'));
            financeSubItems.push(createMenuItem('/fees/structure', <SolutionOutlined />, 'Fee Structure', 'fees'));
            financeSubItems.push(createMenuItem('/fees/heads', <SettingOutlined />, 'Fee Heads', 'fees'));
        }
        if (financeSubItems.length > 0) {
            items.push(createSubMenu('finance-mgmt', <DollarOutlined />, 'Finance & Fees', financeSubItems));
        }

        const receptionItems = [createMenuItem('/reception', <UserOutlined />, 'Visitor Logs', 'reception')];
        items.push(createSubMenu('reception-mgmt', <UserOutlined />, 'Reception & Visitors', receptionItems));

        items.push({ type: 'divider' });

        // Group 5: Campus Facilities
        const facilityItems = [];
        if (isEnabled('library')) {
            facilityItems.push(createSubMenu('library-sub', <ReadOutlined />, t('menu.library'), [
                createMenuItem('/library', <DashboardOutlined />, t('common.dashboard'), 'library'),
                createMenuItem('/library/books', <BookOutlined />, t('menu.books'), 'library'),
                createMenuItem('/library/issue', <SwapOutlined />, t('library.issueBook'), 'library'),
                createMenuItem('/library/return', <HistoryOutlined />, t('library.returnBook'), 'library')
            ]));
        }
        if (isEnabled('hostel')) {
            facilityItems.push(createMenuItem('/hostel', <HomeOutlined />, 'Hostel Portal', 'hostel'));
        }
        if (isEnabled('laundry')) {
            facilityItems.push(createMenuItem('/laundry', <SkinOutlined />, 'Laundry Service', 'laundry'));
        }
        if (isEnabled('hifz')) {
            facilityItems.push(createMenuItem('/hifz', <ReadOutlined />, 'Hifz Program', 'hifz'));
        }
        if (isEnabled('transport')) {
            facilityItems.push(createSubMenu('transport-mgmt', <CarOutlined />, 'Transport & Fleet', [
                createMenuItem('/transport/vehicles', <CarOutlined />, 'Vehicles', 'transport'),
                createMenuItem('/transport/routes', <EnvironmentOutlined />, 'Routes', 'transport'),
                createMenuItem('/transport/assignments', <TeamOutlined />, 'Assignments', 'transport')
            ]));
        }

        if (facilityItems.length > 0) {
            items.push(createSubMenu('facilities-mgmt', <HomeOutlined />, 'Campus Facilities', facilityItems));
        }

        // Group 6: Communications & WhatsApp
        const commSubItems = [];
        commSubItems.push(createMenuItem('/communication', <MessageOutlined />, 'Compose Message', 'communication'));
        commSubItems.push(createMenuItem('/communication/email', <MailOutlined />, 'Email Integration', 'communication'));
        commSubItems.push(createMenuItem('/communication/sms', <MessageOutlined />, 'SMS Integration', 'communication'));
        if (user?.role === 'super_admin' || user?.role === 'developer') {
            commSubItems.push(createMenuItem('/admin/whatsapp', <WhatsAppOutlined />, 'WhatsApp Integration', 'settings'));
        }
        items.push(createSubMenu('communication-mgmt', <MessageOutlined />, 'Communication Hub', commSubItems));

        items.push({ type: 'divider' });

        // Group 7: Analytics & Governance
        const governanceSubItems = [];
        if (isEnabled('reports')) {
            governanceSubItems.push(createMenuItem('/reports', <BarChartOutlined />, t('menu.reports'), 'reports'));
        }
        if (isEnabled('logs')) {
            const showLogs = user?.role !== 'principal' || user?.username === 'developer';
            if (showLogs) {
                governanceSubItems.push(createMenuItem('/logs', <DashOutlined />, t('menu.auditLogs'), 'logs'));
            }
        }
        if (governanceSubItems.length > 0) {
            items.push(createSubMenu('governance-mgmt', <BarChartOutlined />, 'Analytics & Reports', governanceSubItems));
        }

        // Group 8: System Administration
        const sysAdminSubItems = [];
        if (isEnabled('settings')) {
            sysAdminSubItems.push(createMenuItem('/settings', <SettingOutlined />, 'Global Settings', 'settings'));
            sysAdminSubItems.push(createMenuItem('/settings/license', <KeyOutlined />, 'License / Subscription', 'settings'));
        }
        if (user?.role === 'super_admin' || user?.role === 'developer') {
            sysAdminSubItems.push(createMenuItem('/admin/system-control', <LockOutlined />, 'System Control', 'settings'));
        }

        if (sysAdminSubItems.length > 0) {
            items.push(createSubMenu('sys-admin-mgmt', <SettingOutlined />, 'System Administration', sysAdminSubItems));
        }

        return items;

    }, [user, settings, t]);

    return (
        <MainLayout customMenuGenerator={generateAdminMenu}>
            {children}
        </MainLayout>
    );
};

export default AdminLayout;
